import { useLayoutEffect, useRef } from "react";

import { useAppDispatch, useAppSelector } from "store/index";
import {
  selectCurrentlySpeakingClassUserIds,
  selectMaxParticipantBarSlots,
  selectParticipantBarFeaturedClassUserIds,
  selectParticipantBarVisibleClassUserIds,
} from "store/selectors/pubsub-virtual-class-state";
import {
  selectClassUsersFromParticipantBarInputSorted,
  selectParticipantBarInputSource,
} from "store/selectors/virtual-class-users-sorting";
import { PubsubVirtualClassStateActions } from "store/slices/pubsub-virtual-class-state";
import { useAv } from "utils/av";

// Keeps the visible participants list in sync with the calculated list.
export function useLocalParticipantBarUpdateObserver() {
  const dispatch = useAppDispatch();
  const { isInVirtualClass } = useAv();
  const prevSourceRef = useRef<string | null>(null);
  const maxSlots = useAppSelector(selectMaxParticipantBarSlots);
  const currentParticipantBarVisibleIds = useAppSelector(selectParticipantBarVisibleClassUserIds);
  const participantBarInputSource = useAppSelector(selectParticipantBarInputSource).source;
  const displayed = useAppSelector(selectClassUsersFromParticipantBarInputSorted);

  useLayoutEffect(() => {
    if (!isInVirtualClass && prevSourceRef.current !== null) {
      prevSourceRef.current = null;
      return undefined;
    }

    // If the source changed (for instance, in breakouts), we assume the visible participants list is no longer valid and reset it.
    const sourceChanged = prevSourceRef.current !== participantBarInputSource;
    prevSourceRef.current = participantBarInputSource;
    if (sourceChanged && currentParticipantBarVisibleIds.length > 0) {
      dispatch(PubsubVirtualClassStateActions.setParticipantBarVisibleClassUserIds([]));
      return;
    }

    const nextParticipantBarVisibleIds = displayed.slice(0, maxSlots).map((u) => u.id);
    if (
      nextParticipantBarVisibleIds.length === currentParticipantBarVisibleIds.length &&
      nextParticipantBarVisibleIds.every(
        (id, index) => id === currentParticipantBarVisibleIds[index],
      )
    ) {
      return;
    }
    dispatch(
      PubsubVirtualClassStateActions.setParticipantBarVisibleClassUserIds(
        nextParticipantBarVisibleIds,
      ),
    );
  }, [
    isInVirtualClass,
    dispatch,
    maxSlots,
    currentParticipantBarVisibleIds,
    participantBarInputSource,
    displayed,
  ]);
}

// Removes speaking users from the featured participants list.
export function useFeaturedParticipantsObserver() {
  const dispatch = useAppDispatch();
  const { isInVirtualClass } = useAv();

  const featuredParticipants = useAppSelector(selectParticipantBarFeaturedClassUserIds);
  const currentlySpeakingUserIds = useAppSelector(selectCurrentlySpeakingClassUserIds);

  useLayoutEffect(() => {
    if (!isInVirtualClass || !featuredParticipants.length) return;

    const featuredSpeakers = featuredParticipants.filter((id) =>
      currentlySpeakingUserIds.includes(id),
    );

    if (featuredSpeakers.length > 0) {
      const updatedFeatured = featuredParticipants.filter(
        (id) => !currentlySpeakingUserIds.includes(id),
      );

      if (updatedFeatured.length !== featuredParticipants.length) {
        dispatch(
          PubsubVirtualClassStateActions.setParticipantBarFeaturedClassUserIds(updatedFeatured),
        );
      }
    }
  }, [isInVirtualClass, featuredParticipants, currentlySpeakingUserIds, dispatch]);
}
